import {
  type BizcycleProject,
  BusinessSteps,
  type BusinessStep,
  COMPETENCE_STEP,
  formatDate,
  type JiraIssue,
  type Technology,
  todayIso,
} from '@pmai/contracts';
import { Button, Card, cn, EmptyState, IconButton, SelectField } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { JiraKey, useJiraBaseUrl } from '../../entities/jira/jira-links';
import { TechLogo } from '../../entities/technologies/tech-logo';
import { useEnumLabel } from '../../shared/i18n';
import { type IssueSort, SORTS, sortIssues, stepOf } from './bizcycle-model';
import { IssueControls, useIssueUpdate } from './issue-controls';

/** The chosen node's tasks (FR-BIZ-014…016) or, for step 1, the competences (FR-BIZ-017). */
export function StepPanel({
  step,
  issues,
  projects,
  competences,
  technologies,
  canEdit,
}: {
  step: BusinessStep;
  issues: readonly JiraIssue[];
  projects: readonly BizcycleProject[];
  competences: readonly { technologyId: string; projects: BizcycleProject[] }[];
  technologies: readonly Technology[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const title = `${step} ${label(BusinessSteps.byCode(step))}`;
  if (step === COMPETENCE_STEP)
    return <Competences title={title} competences={competences} technologies={technologies} />;
  const list = issues.filter((i) => stepOf(i) === step);
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">
        {t('bizcycle.panelTitle', { step: title, count: list.length })}
      </h2>
      <StepTasks list={list} projects={projects} canEdit={canEdit} />
    </section>
  );
}

function StepTasks({
  list,
  projects,
  canEdit,
}: {
  list: readonly JiraIssue[];
  projects: readonly BizcycleProject[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const baseUrl = useJiraBaseUrl();
  const update = useIssueUpdate();
  const [sort, setSort] = useState<IssueSort>('due');
  const today = todayIso();
  return (
    <>
      <div className="w-52">
        <SelectField
          label={t('bizcycle.sort')}
          value={sort}
          onChange={(e) => setSort(e.target.value as IssueSort)}
          options={SORTS.map((s) => ({ value: s, label: t(`bizcycle.sorts.${s}`) }))}
        />
      </div>
      {list.length === 0 ? (
        <EmptyState icon="list-checks" title={t('bizcycle.emptyStep')} />
      ) : (
        <ul className="flex flex-col gap-2.5">
          {sortIssues(list, sort).map((i) => (
            <li
              key={i.key}
              aria-label={i.key}
              data-burning={i.burning || undefined}
              className={cn(
                'flex flex-col gap-1.5 rounded-card border bg-surface p-3 shadow-sm',
                i.burning ? 'border-orange-400 bg-orange-50' : 'border-border',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">
                  <JiraKey jiraKey={i.key} baseUrl={baseUrl} />
                </span>
                <IconButton
                  icon="flame"
                  label={t('bizcycle.burn', { key: i.key })}
                  aria-pressed={i.burning}
                  disabled={!canEdit}
                  className={i.burning ? 'text-orange-600' : undefined}
                  onClick={() => update.mutate({ params: { key: i.key }, body: { burning: !i.burning } })}
                />
              </div>
              <p className="text-sm">{i.summary}</p>
              <p className="flex flex-wrap gap-x-3 text-xs text-ink-muted">
                <span>👤 {i.assigneeName ?? t('bizcycle.unassigned')}</span>
                <span
                  data-overdue={(i.dueDate && i.dueDate < today) || undefined}
                  className={cn(i.dueDate && i.dueDate < today && 'font-semibold text-red-600')}
                >
                  ⏳ {formatDate(i.dueDate)}
                </span>
                <span>{i.statusName}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <IssueControls issue={i} projects={projects} disabled={!canEdit} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Technologies of the filtered projects; one opens its projects (FR-BIZ-017). */
function Competences({
  title,
  competences,
  technologies,
}: {
  title: string;
  competences: readonly { technologyId: string; projects: BizcycleProject[] }[];
  technologies: readonly Technology[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const tech = (id: string) => technologies.find((x) => x.id === id);
  const chosen = competences.find((c) => c.technologyId === open);
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      {chosen ? (
        <>
          <Button variant="ghost" size="sm" icon="arrow-left" className="w-fit" onClick={() => setOpen(null)}>
            {t('bizcycle.allTechnologies')}
          </Button>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <TechLogo logo={tech(chosen.technologyId)?.logo ?? null} small />
            {t('bizcycle.panelTitle', {
              step: tech(chosen.technologyId)?.name ?? '',
              count: chosen.projects.length,
            })}
          </h2>
          <ul className="flex flex-col gap-2">
            {chosen.projects.map((p) => (
              <li key={p.id} className="rounded-card border border-border bg-surface p-3">
                <Link to={`/projects/${p.id}`} className="font-medium text-brand-700 hover:underline">
                  {p.name}
                </Link>
                <p className="text-xs text-ink-muted">{p.customerName}</p>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h2 className="text-lg font-semibold">
            {t('bizcycle.competencesTitle', { step: title, count: competences.length })}
          </h2>
          {competences.length === 0 ? (
            <Card>
              <p className="text-sm text-ink-muted">{t('bizcycle.noCompetences')}</p>
            </Card>
          ) : (
            <ul className="flex flex-col gap-2">
              {competences.map((c) => (
                <li key={c.technologyId}>
                  <Button
                    variant="secondary"
                    className="w-full justify-between"
                    onClick={() => setOpen(c.technologyId)}
                  >
                    <span className="flex items-center gap-2">
                      <TechLogo logo={tech(c.technologyId)?.logo ?? null} small />
                      {tech(c.technologyId)?.name}
                    </span>
                    <span className="text-xs text-ink-muted">
                      {t('bizcycle.projectCount', { count: c.projects.length })}
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
